import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useResponsiveMap } from '@/hooks/useResponsiveMap';
import { ResponsiveDebugPanel } from './ResponsiveDebugPanel';
import { MapSizeOptimization } from './MapSizeOptimization';

interface ResponsiveDebuggerProps {
  className?: string;
}

export const ResponsiveDebugger: React.FC<ResponsiveDebuggerProps> = ({ className = '' }) => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showOptimizationAnalysis, setShowOptimizationAnalysis] = useState(false);
  const { containerHeight, zoom, center, layoutSize, layoutCenter, fontSize, borderWidth } = useResponsiveMap();

  // 监听窗口大小变化
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 获取屏幕类型
  const getScreenType = () => {
    const { width } = dimensions;
    if (width >= 2560) return '4K超大屏';
    if (width >= 1920) return '1080p大屏';
    if (width >= 1680) return '1050p中屏';
    if (width >= 1366) return '768p标准';
    if (width >= 1024) return '平板横屏';
    return '移动设备';
  };

  // 获取宽高比
  const getAspectRatio = () => {
    if (dimensions.height === 0) return '0:0';
    const ratio = dimensions.width / dimensions.height;
    return `${ratio.toFixed(2)}:1`;
  };

  return (
    <div className={`relative ${className}`}>
      {/* 简化显示 */}
      <motion.div
        className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm font-medium cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        📱 {dimensions.width}×{dimensions.height}
      </motion.div>

      {/* 详细信息面板 */}
      {isExpanded && (
        <motion.div
          className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 min-w-80"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <div className="space-y-3">
            {/* 标题 */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">响应式调试信息</h3>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* 屏幕信息 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">屏幕信息</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">分辨率:</span>
                    <span className="font-mono">{dimensions.width}×{dimensions.height}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">类型:</span>
                    <span className="text-blue-600">{getScreenType()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">宽高比:</span>
                    <span className="font-mono">{getAspectRatio()}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-gray-700">地图配置</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">容器高度:</span>
                    <span className="font-mono">{containerHeight}px</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">布局尺寸:</span>
                    <span className="font-mono text-green-600">{layoutSize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">布局中心:</span>
                    <span className="font-mono text-purple-600">[{layoutCenter[0]}, {layoutCenter[1]}]</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">缩放级别:</span>
                    <span className="font-mono">{zoom.toFixed(1)}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">中心点:</span>
                    <span className="font-mono">[{center[0]}, {center[1]}]</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">字体大小:</span>
                    <span className="font-mono">{fontSize}px</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">边框宽度:</span>
                    <span className="font-mono">{borderWidth}px</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 详细调试按钮 */}
            <div className="pt-2 border-t border-gray-200 space-y-2">
              <button
                onClick={() => {
                  setShowDetailPanel(true);
                  setIsExpanded(false);
                }}
                className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
              >
                🔍 打开详细调试面板
              </button>
              
              <button
                onClick={() => {
                  setShowOptimizationAnalysis(true);
                  setIsExpanded(false);
                }}
                className="w-full px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 transition-colors"
              >
                📊 查看尺寸优化分析
              </button>
            </div>

            {/* 实时更新提示 */}
            <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
              💡 调整浏览器窗口大小查看实时变化
            </div>
          </div>
        </motion.div>
      )}

      {/* 详细调试面板 */}
      <ResponsiveDebugPanel 
        isVisible={showDetailPanel}
        onClose={() => setShowDetailPanel(false)}
      />

      {/* 地图尺寸优化分析 */}
      <MapSizeOptimization 
        isVisible={showOptimizationAnalysis}
        onClose={() => setShowOptimizationAnalysis(false)}
      />
    </div>
  );
};
